/**
 * Display labels for the company permissions catalogue (`company/permissions`).
 * Backend keys stay as `files.*`; these helpers clarify that the files group
 * governs the company's file library (business-admin RBAC), not tradie-owned files.
 */

/** Retired keys — hidden in Settings until removed from the backend catalogue. */
export const DEPRECATED_PERMISSION_KEYS = [
  'requests.respond',
  'settings.billing.manage',
  'settings.roles.manage',
  'files.approve',
]

export function isDeprecatedPermissionKey(key) {
  return DEPRECATED_PERMISSION_KEYS.includes(key)
}

export function filterPermissionCatalogue(catalogue) {
  if (!Array.isArray(catalogue)) return []

  return catalogue
    .map((group) => ({
      ...group,
      permissions: (group.permissions || []).filter(
        (perm) => !isDeprecatedPermissionKey(perm.key),
      ),
    }))
    .filter((group) => group.permissions.length > 0)
}

// Both file groups (a tradie's files on a project, and the project's own
// documents) are shown under the one "Project tradies & files" heading.
const MERGED_INTO_PROJECT_TRADIES = ['projects.tradie.files', 'projects.files']

export function mergeProjectGroups(catalogue) {
  const fileGroups = catalogue.filter((g) =>
    MERGED_INTO_PROJECT_TRADIES.includes(g.group),
  )
  if (!fileGroups.length) return catalogue
  const filePermissions = fileGroups.reduce(
    (all, g) => all.concat(g.permissions),
    [],
  )
  return catalogue
    .filter((g) => !MERGED_INTO_PROJECT_TRADIES.includes(g.group))
    .map((g) =>
      g.group === 'projects.tradies'
        ? { ...g, permissions: [...g.permissions, ...filePermissions] }
        : g,
    )
}

export function stripDeprecatedPermissionIds(catalogue, permissionIds) {
  if (!Array.isArray(permissionIds)) return []

  const allowedIds = new Set()
  filterPermissionCatalogue(catalogue).forEach((group) => {
    group.permissions.forEach((perm) => {
      if (perm.id != null) allowedIds.add(perm.id)
    })
  })

  return permissionIds.filter((id) => allowedIds.has(id))
}

export function permissionGroupLabel(group) {
  if (!group) return ''
  if (group === 'files') return 'Company files'
  if (group === 'projects') return 'Projects'
  if (group === 'projects.tradies') return 'Project tradies & files'
  if (group === 'timesheets') return 'Timesheets'
  if (group === 'settings') return 'Settings'
  if (group === 'notifications') return 'Notifications'
  return group.charAt(0).toUpperCase() + group.slice(1)
}

export function permissionItemLabel(perm) {
  if (!perm) return ''
  const label = perm.label || perm.key || ''
  const key = perm.key || ''

  if (key.startsWith('files.') && !/company files/i.test(label)) {
    return label.replace(/\bfiles\b/i, 'company files')
  }

  return label
}

const GROUP_HINTS = {
  notifications:
    'Controls who can send mass push notifications to tradies and project tradies.',
  home: 'Controls what business admins can see on the home dashboard.',
  files: 'Controls what business admins can do in the company file library.',
  requests:
    'Controls what business admins can do with requests (view, create, delete, view message logs).',
  tradies: 'Controls what business admins can do with tradies and their files.',
  projects:
    'Controls what business admins can do with projects (create, edit, delete, archive, export, share).',
  'projects.tradies':
    'Controls what business admins can do with tradies and their files inside a project.',
  trades:
    'Controls what business admins can do with trades (skills and licence categories).',
  timesheets:
    'Controls what business admins can do with timesheet entries (view, create, edit, delete, download).',
  settings:
    'Controls what business admins can access in account settings (manage users, view logs, manage roles & permissions, manage billing & tokens).',
}

export function permissionGroupHint(group) {
  return GROUP_HINTS[group] || null
}

// Named exports kept for any existing direct imports.
export const FILES_PERMISSION_GROUP_HINT = GROUP_HINTS.files
export const PROJECTS_PERMISSION_GROUP_HINT = GROUP_HINTS.projects
export const PROJECTS_TRADIES_PERMISSION_GROUP_HINT =
  GROUP_HINTS['projects.tradies']
export const PROJECTS_TRADIE_FILES_PERMISSION_GROUP_HINT =
  GROUP_HINTS['projects.tradie.files']
export const TIMESHEETS_PERMISSION_GROUP_HINT = GROUP_HINTS.timesheets
export const SETTINGS_PERMISSION_GROUP_HINT = GROUP_HINTS.settings
